import usePrevious from '@liveauctioneers/caterwaul-components/lib/hooks/usePrevious';
import { normalizeQuery } from '@liveauctioneers/caterwaul-components/lib/InboxMessenger/utils/urls';
import { Button } from '@liveauctioneers/hammer-ui-core/button';
import { FormattedMessage, useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { toast } from '@liveauctioneers/hammer-ui-core/toast';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import CategoryAd from '@/components/Ads/CategoryAd';
import EnsureLoggedInContainer from '@/components/EnsureLoggedIn/EnsureLoggedInContainer';
import { InboxFolderStats } from '@/components/InboxMessenger/components/Folders/types/InboxFolderStats';
import { Conversations, InboxLayout } from '@/components/InboxMessenger/InboxMessenger';
import PageTitle from '@/components/PageTitle/PageTitle';
import useWindowSize from '@/hooks/useWindowSize';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { LeaderBoardAdTypes } from '@/redux/modules/catalog/ad/catalogAd.types';
import { getBaseUrl } from '@/redux/modules/config';
import {
    getConversationPagination,
    getConversations,
    getConversationsLoading,
} from '@/redux/modules/conversation/conversations/conversation.selectors';
import {
    archiveConversationFolder,
    createConversationFolder,
    renameConversationFolder,
} from '@/redux/modules/conversation/folders/conversationFolders.actions';
import { getConversationFolders } from '@/redux/modules/conversation/folders/conversationFolders.selectors';
import { loadConversationPageData } from '@/redux/modules/conversation/page/conversationPage.actions';
import { Folder } from '@/types/Messaging';
import { parseQueryParams } from '@/utils/queryParams';
import { getMessagesUrl, toQueryString } from '@/utils/urls';

import MessagesPageHeaderMeta from './MessagesPageHeaderMeta';

const DEFAULT_PAGE_SIZE = 48;

const MessagesPage = () => {
    const baseUrl = useAppSelector(getBaseUrl);
    const conversations = useAppSelector(getConversations);
    const conversationsLoading = useAppSelector(getConversationsLoading);
    const { width } = useWindowSize();
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { formatMessage, formatNumber } = useIntl();

    const queryParams = new URLSearchParams(location.search);
    const activeFolder = queryParams.get('folderId') || 0;
    const params: any = parseQueryParams(location.search);

    const setActiveInbox = (folderId) => {
        // we need to update the folderId and reset the page to 1
        const newParams = normalizeQuery({
            ...params,
            filter: 'all',
            folderId,
            page: 1,
            search: '',
        });
        navigate({
            pathname: getMessagesUrl(),
            search: toQueryString(newParams),
        });
    };

    const navigateTo = (param: any) => {
        const newParams = normalizeQuery({ ...params, ...param });
        navigate({ search: toQueryString(newParams) });
    };

    const searchParam = params.search;
    const previousSearch = usePrevious(searchParam);

    const onSearch = (search: string) => {
        const params: any = { search };
        if ((!previousSearch && Boolean(search)) || (Boolean(previousSearch) && !search)) {
            params.page = 1;
        }
        return navigateTo(params);
    };

    const onFilter = (filter: string) => {
        navigateTo({ filter });
    };

    // Get the messenger url for each conversation
    const getMessengerUrl = (convId) => {
        const queryString = toQueryString(params);
        return `${getMessagesUrl()}/${convId}${queryString ? `?${queryString}` : ''}`;
    };

    // Filters for pagination
    const { totalRecords } = useAppSelector(getConversationPagination);

    const inboxFolders: InboxFolderStats = useAppSelector(getConversationFolders);
    const folders = useMemo(
        () => [...inboxFolders.systemFolders, ...inboxFolders.userFolders],
        [inboxFolders.systemFolders, inboxFolders.userFolders]
    );

    let numberActiveFolder = null;
    if (!params.search) {
        numberActiveFolder = Number(activeFolder);
    }

    const archiveFolder = useCallback((folderId: number) => dispatch(archiveConversationFolder(folderId)), [dispatch]);

    const createFolder = useCallback((name: string) => dispatch(createConversationFolder(name)), [dispatch]);

    const renameFolder = useCallback(
        (folderId: number, name: string) => dispatch(renameConversationFolder({ folderId, name })),
        [dispatch]
    );

    const convoActionArchive = useCallback(
        (conversationIds) =>
            dispatch(
                loadConversationPageData({
                    action: 'archive',
                    conversationIds,
                    query: params,
                })
            ),
        [dispatch, params]
    );

    const convoActionMarkRead = useCallback(
        (conversationIds) =>
            dispatch(
                loadConversationPageData({
                    action: 'markRead',
                    conversationIds,
                    query: params,
                })
            ),
        [dispatch, params]
    );

    const convoActionMarkUnread = useCallback(
        (conversationIds) =>
            dispatch(
                loadConversationPageData({
                    action: 'markUnread',
                    conversationIds,
                    query: params,
                })
            ),
        [dispatch, params]
    );

    const convoActionMove = useCallback(
        (conversationIds: number[], folder: Folder) => {
            const { id: folderId, name: folderName } = folder;
            dispatch(
                loadConversationPageData({
                    action: 'move',
                    conversationIds,
                    folderId,
                    markOnly: false,
                    query: params,
                })
            );
            toast('success', {
                description: formatMessage(
                    {
                        id: 'toastMessages.descriptions.conversationMovedToFolder',
                    },
                    { folderName }
                ),
                title: formatMessage({ id: 'toastMessages.titles.success' }),
            });
        },
        [dispatch, params, formatMessage]
    );

    const adId = 'messages';
    const adPageName = `leaderboard-${adId}`;

    // Prevent Messenger Page from trying to load SSR
    const [hasRendered, setRendered] = useState(false);
    useEffect(() => setRendered(true), [hasRendered]);

    if (!hasRendered) {
        return null;
    }

    return (
        <EnsureLoggedInContainer>
            <MessagesPageHeaderMeta baseUrl={baseUrl} />
            <CategoryAd
                adType={LeaderBoardAdTypes[adId]}
                pageName={adPageName}
            />
            <StyledPageTitle title={<FormattedMessage id="messaging.title" />} />
            {width <= 900 && (
                <StyledTemporaryMobileNavigation>
                    <Button
                        onClick={() => setActiveInbox(0)}
                        size="sm"
                        variant={numberActiveFolder !== 0 ? 'outlined' : 'filled'}
                    >
                        <FormattedMessage id="messaging.inbox" />
                    </Button>
                    <Button
                        onClick={() => setActiveInbox(1)}
                        size="sm"
                        variant={numberActiveFolder !== 1 ? 'outlined' : 'filled'}
                    >
                        <FormattedMessage id="messaging.sent" />
                    </Button>
                    <Button
                        onClick={() => setActiveInbox(-1)}
                        size="sm"
                        variant={numberActiveFolder !== -1 ? 'outlined' : 'filled'}
                    >
                        <FormattedMessage id="messaging.archive" />
                    </Button>
                </StyledTemporaryMobileNavigation>
            )}
            <StyledInboxLayout
                activeFolder={numberActiveFolder}
                formatNumber={formatNumber}
                hideLeftNav={width < 900}
                inboxFolders={inboxFolders}
                loading={conversationsLoading}
                onArchiveFolder={archiveFolder}
                onCreateFolder={createFolder}
                onRenameFolder={renameFolder}
                onSearch={onSearch}
                onSetActiveInbox={setActiveInbox}
            >
                <Conversations
                    activeFolder={numberActiveFolder}
                    archiveLabel={<FormattedMessage id="messaging.archive" />}
                    // @ts-expect-error
                    // this component uses "ComponentType" but the slice was previously using "any"
                    //   but it was actually hiding the "FetchConversationResponsePayload" type
                    conversations={conversations}
                    conversationsLoading={conversationsLoading}
                    filter={params.filter || 'all'}
                    folders={folders}
                    formatNumber={formatNumber}
                    getMessengerUrl={getMessengerUrl}
                    location={location}
                    markReadLabel={<FormattedMessage id="messaging.markRead" />}
                    markUnreadLabel={<FormattedMessage id="messaging.markUnread" />}
                    mobile={width < 700}
                    moveLabel={<FormattedMessage id="messaging.move" />}
                    onArchive={convoActionArchive}
                    onChangePagination={navigateTo}
                    onFilter={onFilter}
                    onMarkRead={convoActionMarkRead}
                    onMarkUnread={convoActionMarkUnread}
                    onMove={convoActionMove}
                    paginationFilter={{
                        page: params.page || 1,
                        pageSize: params.pageSize || DEFAULT_PAGE_SIZE,
                        totalRecords,
                    }}
                />
            </StyledInboxLayout>
        </EnsureLoggedInContainer>
    );
};

export default MessagesPage;

const StyledInboxLayout = styled(InboxLayout)`
    padding: 0 20px;
`;

const StyledPageTitle = styled(PageTitle)`
    && {
        width: 100%;
        max-width: ${({ theme }) => theme.breakpoints.pageWidth};
        padding: 0 20px;
        margin: 32px auto 24px;
    }
`;

const StyledTemporaryMobileNavigation = styled.div`
    display: flex;
    flex-direction: row;
    margin: 0 20px;
    justify-content: flex-start;
    align-items: center;

    & > button {
        margin: 0 10px 10px 0;
        width: calc(100% - 40px - 10px);

        &:last-child {
            margin-right: 0;
        }
    }
`;
